import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { IntlProvider } from "react-intl";

import {
  detectLocale,
  DEFAULT_LOCALE,
  getMessages,
  setLocale as persistLocale,
  type SupportedLocale,
} from "@/i18n";

interface LocaleContextShape {
  locale: SupportedLocale;
  setLocale: (l: SupportedLocale) => void;
}

const LocaleContext = createContext<LocaleContextShape>({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
});

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<SupportedLocale>(() => detectLocale());

  const setLocale = useCallback((l: SupportedLocale) => {
    persistLocale(l);
    setLocaleState(l);
  }, []);

  const messages = useMemo(() => getMessages(locale), [locale]);

  return (
    <LocaleContext.Provider value={{ locale, setLocale }}>
      <IntlProvider
        locale={locale}
        defaultLocale={DEFAULT_LOCALE}
        messages={messages}
        onError={(err) => {
          // Silenciar MISSING_TRANSLATION / MISSING_DATA — tenemos fallback al default.
          if (err.code === "MISSING_TRANSLATION" || err.code === "MISSING_DATA") return;
          console.error("intl", err);
        }}
      >
        {children}
      </IntlProvider>
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  return useContext(LocaleContext);
}
